import { create } from 'zustand';
import { applyNodeChanges, applyEdgeChanges } from 'reactflow';

const createDefaultFields = () => [
  { name: 'id', type: 'UUID', nullable: false, unique: false },
  { name: '', type: 'String', nullable: true, unique: false },
  { name: '', type: 'String', nullable: true, unique: false },
];

const createDefaultEnumFields = () => [
  { name: 'VALUE_1' },
  { name: 'VALUE_2' },
  { name: 'VALUE_3' },
];

let nodeIdCounter = 1;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const MAX_UNDO = 5;

const useStore = create((set, get) => ({
  nodes: [],
  edges: [],
  theme: localStorage.getItem('theme') || 'dark',
  uiLang: localStorage.getItem('uiLang') || 'en',

  // Save user config to backend
  _saveConfig: () => {
    const { accessToken, theme, uiLang } = get();
    if (!accessToken) return;
    const config = JSON.stringify({ theme, uiLang });
    fetch(`${API_BASE}/api/v1/users/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: config,
    }).catch(() => {});
  },

  // Load user config from backend
  loadUserConfig: async () => {
    const { accessToken } = get();
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/config`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        const config = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
        if (config.theme) {
          localStorage.setItem('theme', config.theme);
          set({ theme: config.theme });
        }
        if (config.uiLang) {
          localStorage.setItem('uiLang', config.uiLang);
          set({ uiLang: config.uiLang });
        }
      }
    } catch { /* ignore */ }
  },

  setUiLang: (lang) => {
    localStorage.setItem('uiLang', lang);
    set({ uiLang: lang });
    setTimeout(() => get()._saveConfig(), 0);
  },

  // Undo history
  _undoStack: [],
  _undoPaused: false,

  pushUndo: () => {
    if (get()._undoPaused) return;
    const { nodes, edges } = get();
    set((s) => ({
      _undoStack: [...s._undoStack.slice(-(MAX_UNDO - 1)), { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }],
    }));
  },

  undo: () => {
    const stack = get()._undoStack;
    if (stack.length === 0) return;
    const prev = stack[stack.length - 1];
    set({
      nodes: prev.nodes,
      edges: prev.edges,
      _undoStack: stack.slice(0, -1),
      selectedNodeId: null,
      selectedFieldIndex: null,
      editingCell: null,
    });
  },

  // Read-only mode (used by admin viewer)
  readOnly: false,
  setReadOnly: (readOnly) => set({ readOnly }),

  // Template-edit mode (admin editing a template, no generate/auto-save)
  templateMode: false,
  setTemplateMode: (templateMode) => set({ templateMode }),

  // Auth
  accessToken: localStorage.getItem('accessToken') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  userEmail: null,
  userName: null,
  setToken: (accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    set({ accessToken, ...(refreshToken ? { refreshToken } : {}) });
  },
  loadUserMe: async () => {
    const { accessToken } = get();
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/me`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        set({ userEmail: data.data.email || null, userName: data.data.name || null });
      }
    } catch { /* ignore */ }
  },
  isLoggedIn: () => !!get().accessToken,
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('adminToken');
    set({ accessToken: null, refreshToken: null, userEmail: null, userName: null });
  },

  // Project ID (for update tracking)
  projectId: null,

  // Project config
  projectName: 'demo-project',
  basePackage: 'com.example',
  language: 'java',
  buildTool: 'maven',
  javaVersion: '17',
  springBootVersion: '',
  packaging: 'jar',

  // Database config
  database: 'postgresql',
  dbUsername: 'postgres',
  dbPassword: 'postgres',

  // Security / JWT
  jwtSecretKey: '',
  jwtAutoGenerate: true,
  jwtExpiration: '86400000',

  // Features
  features: {
    dto: true,
    service: true,
    controller: true,
    swagger: true,
    security: false,
    oauth2: false,
    minio: false,
    cors: true,
  },

  // Available versions (fetched from APIs)
  javaVersions: ['17', '21'],
  springBootVersions: [],

  // Setters
  setProjectConfig: (key, value) => set({ [key]: value }),

  // Reset editor to blank state
  resetEditor: () => set({
    nodes: [],
    edges: [],
    projectId: null,
    projectName: 'demo-project',
    basePackage: 'com.example',
    language: 'java',
    buildTool: 'maven',
    javaVersion: '17',
    springBootVersion: '',
    packaging: 'jar',
    database: 'postgresql',
    dbUsername: 'postgres',
    dbPassword: 'postgres',
    jwtSecretKey: '',
    jwtAutoGenerate: true,
    jwtExpiration: '86400000',
    features: { dto: true, service: true, controller: true, swagger: true, security: false, oauth2: false, minio: false, cors: true },
    selectedNodeId: null,
    selectedFieldIndex: null,
    editingCell: null,
    pendingConnection: null,
  }),

  // Restore full state from saved JSON (entities → nodes, relationships → edges)
  restoreFromJSON: (json) => {
    set({ _undoPaused: true });
    const state = get();
    state.resetEditor();

    // Project config
    if (json.project) {
      set({
        projectName: json.project.name || 'demo-project',
        basePackage: json.project.basePackage || 'com.example',
        projectId: json.project.projectId || null,
        buildTool: json.project.buildTool || 'maven',
        javaVersion: json.project.javaVersion || '17',
        springBootVersion: json.project.springBootVersion || '',
        packaging: json.project.packaging || 'jar',
      });
      if (json.project.language) {
        // Use setLanguage to properly set language
        get().setLanguage(json.project.language);
      }
    }

    // Database
    if (json.database) {
      set({
        database: json.database.type || 'postgresql',
        dbUsername: json.database.username || '',
        dbPassword: json.database.password || '',
      });
    }

    // Features
    if (json.features) {
      set({ features: { ...get().features, ...json.features } });
    }

    // Security
    if (json.security?.jwt) {
      set({
        jwtSecretKey: json.security.jwt.secretKey || '',
        jwtAutoGenerate: !!json.security.jwt.autoGenerate,
        jwtExpiration: String(json.security.jwt.expirationMs || '86400000'),
      });
    }

    // Rebuild nodes from entities
    const nodes = [];
    const nodeIdMap = {}; // entityName → nodeId
    let counter = 1;

    // First pass: find enum types from entity fields (classType === 'ENUM')
    const enumsFromFields = {};
    if (json.entities) {
      for (const entity of json.entities) {
        for (const field of entity.fields || []) {
          if (field.classType === 'ENUM' && field.enumValues) {
            enumsFromFields[field.type] = field.enumValues;
          }
        }
      }
    }

    // Create enum nodes - use _meta.enums for positions and values
    const savedEnums = json._meta?.enums || [];
    const savedEnumMap = {};
    for (const se of savedEnums) {
      savedEnumMap[se.name] = se;
    }

    // Merge: enums from fields + enums from _meta (unlinked ones)
    const allEnums = { ...enumsFromFields };
    for (const se of savedEnums) {
      if (!allEnums[se.name]) {
        allEnums[se.name] = se.values || [];
      }
    }

    let enumIdx = 0;
    for (const [enumName, values] of Object.entries(allEnums)) {
      const id = `enum_${counter++}`;
      nodeIdMap[enumName] = id;
      const saved = savedEnumMap[enumName];
      nodes.push({
        id,
        type: 'enum',
        position: saved?.position || { x: 500 + enumIdx * 220, y: 60 },
        data: {
          name: enumName,
          tableType: 'enum',
          fields: (saved?.values || values).map((v) => ({ name: v })),
        },
      });
      enumIdx++;
    }

    // Create entity nodes
    if (json.entities) {
      json.entities.forEach((entity, i) => {
        const id = `entity_${counter++}`;
        nodeIdMap[entity.name] = id;
        const fields = (entity.fields || []).map((f) => ({
          name: f.name,
          type: f.type,
          nullable: !!f.nullable,
          unique: !!f.unique,
        }));
        nodes.push({
          id,
          type: 'entity',
          position: entity._position || { x: 60 + (i % 3) * 280, y: 60 + Math.floor(i / 3) * 250 },
          data: {
            name: entity.name,
            tableType: 'entity',
            fields: fields.length > 0 ? fields : [{ name: 'id', type: 'UUID', nullable: false, unique: false }],
          },
        });
      });
    }

    // Create edges from _meta.edges (new format) or fallback to old format
    const edges = [];
    if (json._meta?.edges?.length) {
      // New format: all edges saved directly with names
      for (const ee of json._meta.edges) {
        const srcId = nodeIdMap[ee.source];
        const tgtId = nodeIdMap[ee.target];
        if (!srcId || !tgtId) continue;
        edges.push({
          id: `e-${srcId}-${tgtId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          source: srcId,
          sourceHandle: ee.sourceHandle,
          target: tgtId,
          targetHandle: ee.targetHandle,
          type: 'relationship',
          data: {
            sourceType: ee.sourceType || 'One',
            targetType: ee.targetType || 'One',
            midX: ee.midX,
            midY: ee.midY,
          },
        });
      }
    } else {
      // Fallback for old projects: restore from relationships + enumEdges
      const seen = new Set();
      if (json.entities) {
        for (const entity of json.entities) {
          const sourceNodeId = nodeIdMap[entity.name];
          for (const rel of entity.relationships || []) {
            const targetNodeId = nodeIdMap[rel.target];
            if (!sourceNodeId || !targetNodeId) continue;

            // Deduplicate (OneToOne stored from both sides)
            const edgeKey = [sourceNodeId, targetNodeId].sort().join('::');
            if (seen.has(edgeKey)) continue;
            seen.add(edgeKey);

            let sourceType = 'One';
            let targetType = 'One';
            if (rel.type === 'ManyToOne') { sourceType = 'Many'; targetType = 'One'; }
            else if (rel.type === 'OneToMany') { sourceType = 'One'; targetType = 'Many'; }

            edges.push({
              id: `e-${sourceNodeId}-${targetNodeId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              source: sourceNodeId,
              sourceHandle: rel._sourceHandle || `field-0-right`,
              target: targetNodeId,
              targetHandle: rel._targetHandle || `field-0-left`,
              type: 'relationship',
              data: { sourceType, targetType },
            });
          }
        }
      }
      // Old enum edges
      if (json._meta?.enumEdges) {
        for (const ee of json._meta.enumEdges) {
          const srcId = nodeIdMap[ee.source];
          const tgtId = nodeIdMap[ee.target];
          if (!srcId || !tgtId) continue;
          edges.push({
            id: `e-${srcId}-${tgtId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            source: srcId,
            sourceHandle: ee.sourceHandle,
            target: tgtId,
            targetHandle: ee.targetHandle,
            type: 'relationship',
            data: { sourceType: 'Enum', targetType: 'Enum' },
          });
        }
      }
    }

    set({ nodes, edges, _undoPaused: false, _undoStack: [] });

    // Update nodeIdCounter to avoid ID collisions with new nodes
    nodeIdCounter = counter;
  },
  selectedNodeId: null,
  selectedFieldIndex: null,

  // Editing state: { nodeId, fieldIndex, part: 'name'|'type'|'tableName' } or null
  editingCell: null,
  setEditingCell: (cell) => set({ editingCell: cell }),
  clearEditing: () => set({ editingCell: null }),

  // Swap two fields within a node
  swapFields: (nodeId, fromIndex, toIndex) => {
    get().pushUndo();
    set((s) => {
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node || fromIndex === toIndex) return s;
      if (node.data.tableType === 'entity' && (fromIndex === 0 || toIndex === 0)) return s;
      const newFields = [...node.data.fields];
      const temp = newFields[fromIndex];
      newFields[fromIndex] = newFields[toIndex];
      newFields[toIndex] = temp;
      return {
        nodes: s.nodes.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, fields: newFields } } : n
        ),
        selectedFieldIndex: toIndex,
      };
    });
  },

  // Pending connection (for relationship popup)
  pendingConnection: null,
  setPendingConnection: (conn) => set({ pendingConnection: conn }),

  // Theme
  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);
    set({ theme: newTheme });
    setTimeout(() => get()._saveConfig(), 0);
  },

  // Relation hover preview mode: 'code' (Java class) or 'json' (API response shape)
  relationHoverMode: localStorage.getItem('relationHoverMode') || 'code',
  setRelationHoverMode: (mode) => {
    localStorage.setItem('relationHoverMode', mode);
    set({ relationHoverMode: mode });
  },

  // Language & DB
  setLanguage: (language) => {
    set({ language });
    // Update default types when language changes
    const defaultType = language === 'java' ? 'String' : 'str';
    set((s) => ({
      nodes: s.nodes.map((node) => {
        if (node.data.tableType === 'enum') return node;
        return {
          ...node,
          data: {
            ...node.data,
            fields: node.data.fields.map((f) => ({
              ...f,
              type: f.type || defaultType,
            })),
          },
        };
      }),
    }));
  },
  setDatabase: (database) => set({ database }),

  // Features
  toggleFeature: (feature) =>
    set((s) => {
      const updated = { ...s.features, [feature]: !s.features[feature] };
      // Security o'chirilsa cors ham o'chsin
      if (feature === 'security' && !updated.security) {
        updated.cors = false;
      }
      return { features: updated };
    }),

  // Selection
  setSelectedNode: (nodeId, fieldIndex = null) =>
    set({ selectedNodeId: nodeId, selectedFieldIndex: fieldIndex }),
  clearSelection: () => set({ selectedNodeId: null, selectedFieldIndex: null }),

  // Node operations
  addEntityNode: (position) => {
    get().pushUndo();
    const id = `entity_${nodeIdCounter++}`;
    const newNode = {
      id,
      type: 'entity',
      position: position || { x: 100 + Math.random() * 400, y: 100 + Math.random() * 300 },
      data: {
        name: 'new_table',
        tableType: 'entity',
        fields: createDefaultFields(),
      },
    };
    set((s) => ({ nodes: [...s.nodes, newNode] }));
    return id;
  },

  addEnumNode: (position) => {
    get().pushUndo();
    const id = `enum_${nodeIdCounter++}`;
    const newNode = {
      id,
      type: 'enum',
      position: position || { x: 100 + Math.random() * 400, y: 100 + Math.random() * 300 },
      data: {
        name: 'new_enum',
        tableType: 'enum',
        fields: createDefaultEnumFields(),
      },
    };
    set((s) => ({ nodes: [...s.nodes, newNode] }));
    return id;
  },

  deleteNode: (nodeId) => {
    get().pushUndo();
    set((s) => ({
      nodes: s.nodes.filter((n) => n.id !== nodeId),
      edges: s.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNodeId: s.selectedNodeId === nodeId ? null : s.selectedNodeId,
    }));
  },

  // Update node data
  updateNodeData: (nodeId, updater) =>
    set((s) => ({
      nodes: s.nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: typeof updater === 'function' ? updater(node.data) : { ...node.data, ...updater } }
          : node
      ),
    })),

  // Update nodes (position, dimensions, select, remove etc.)
  onNodesChange: (changes) =>
    set((s) => ({ nodes: applyNodeChanges(changes, s.nodes) })),

  // Field operations
  updateField: (nodeId, fieldIndex, updates) =>
    set((s) => ({
      nodes: s.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const newFields = [...node.data.fields];
        newFields[fieldIndex] = { ...newFields[fieldIndex], ...updates };
        return { ...node, data: { ...node.data, fields: newFields } };
      }),
    })),

  addFieldAbove: (nodeId, fieldIndex) => {
    get().pushUndo();
    set((s) => {
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node) return s;
      const isEnum = node.data.tableType === 'enum';
      const newField = isEnum
        ? { name: 'NEW_VALUE' }
        : { name: '', type: s.language === 'java' ? 'String' : 'str', nullable: true, unique: false };
      const newFields = [...node.data.fields];
      newFields.splice(fieldIndex, 0, newField);
      return {
        nodes: s.nodes.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, fields: newFields } } : n
        ),
      };
    });
  },

  addFieldBelow: (nodeId, fieldIndex) => {
    get().pushUndo();
    set((s) => {
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node) return s;
      const isEnum = node.data.tableType === 'enum';
      const newField = isEnum
        ? { name: 'NEW_VALUE' }
        : { name: '', type: s.language === 'java' ? 'String' : 'str', nullable: true, unique: false };
      const newFields = [...node.data.fields];
      newFields.splice(fieldIndex + 1, 0, newField);
      return {
        nodes: s.nodes.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, fields: newFields } } : n
        ),
        selectedFieldIndex: fieldIndex + 1,
      };
    });
  },

  deleteField: (nodeId, fieldIndex) => {
    get().pushUndo();
    set((s) => {
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node || node.data.fields.length <= 1) return s;
      // Don't delete id field (index 0) for entity tables
      if (node.data.tableType === 'entity' && fieldIndex === 0) return s;
      const newFields = node.data.fields.filter((_, i) => i !== fieldIndex);
      return {
        nodes: s.nodes.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, fields: newFields } } : n
        ),
        selectedFieldIndex: null,
      };
    });
  },

  // Set field count (add/remove from end)
  setFieldCount: (nodeId, count) => {
    get().pushUndo();
    set((s) => {
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node || count < 1) return s;
      const isEnum = node.data.tableType === 'enum';
      const isEntity = node.data.tableType === 'entity';
      const minCount = isEntity ? 1 : 1; // at least id for entity
      if (count < minCount) return s;
      const newFields = [...node.data.fields];
      if (count > newFields.length) {
        const toAdd = count - newFields.length;
        for (let i = 0; i < toAdd; i++) {
          newFields.push(
            isEnum
              ? { name: 'NEW_VALUE' }
              : { name: '', type: s.language === 'java' ? 'String' : 'str', nullable: false, unique: false }
          );
        }
      } else if (count < newFields.length) {
        // Remove from end, but never remove id (index 0) for entities
        while (newFields.length > count && newFields.length > minCount) {
          newFields.pop();
        }
      }
      return {
        nodes: s.nodes.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, fields: newFields } } : n
        ),
      };
    });
  },

  // Edge operations
  addEdge: (edge) => {
    get().pushUndo();
    set((s) => ({ edges: [...s.edges, edge] }));
  },

  updateEdgeData: (edgeId, newData) => set((s) => ({
    edges: s.edges.map((e) => e.id === edgeId ? { ...e, data: { ...e.data, ...newData } } : e),
  })),

  deleteEdge: (edgeId) =>
    set((s) => ({
      edges: s.edges.filter((e) => e.id !== edgeId),
    })),

  deleteEdges: (edgeIds) => {
    if (!edgeIds?.length) return;
    const idSet = new Set(edgeIds);
    set((s) => ({
      edges: s.edges.filter((e) => !idSet.has(e.id)),
    }));
  },

  onEdgesChange: (changes) =>
    set((s) => ({ edges: applyEdgeChanges(changes, s.edges) })),

  setEdges: (edges) => set({ edges }),

  // Get all entity names (for type autocomplete)
  getEntityNames: () => {
    return get()
      .nodes.filter((n) => n.data.tableType === 'entity' || n.data.tableType === 'enum')
      .map((n) => n.data.name)
      .filter((name) => name && name !== 'new_table' && name !== 'new_enum');
  },

  // Generate JSON
  generateJSON: () => {
    const state = get();

    // Collect enum nodes for classType detection
    const enumNodes = state.nodes.filter((n) => n.data.tableType === 'enum');
    const enumMap = {};
    for (const en of enumNodes) {
      enumMap[en.data.name] = en.data.fields.map((f) => f.name);
    }

    // Collect entity names for classType detection
    const entityNames = new Set(
      state.nodes.filter((n) => n.data.tableType === 'entity').map((n) => n.data.name)
    );

    const entities = [];

    // Build _meta for frontend-only data (positions, enums, edges)
    // Save ALL edges with node names for reliable restoration
    const allEdges = state.edges.map((e) => {
      const srcNode = state.nodes.find((n) => n.id === e.source);
      const tgtNode = state.nodes.find((n) => n.id === e.target);
      return {
        source: srcNode?.data?.name,
        target: tgtNode?.data?.name,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        sourceType: e.data?.sourceType || 'One',
        targetType: e.data?.targetType || 'One',
        midX: e.data?.midX,
        midY: e.data?.midY,
      };
    }).filter((e) => e.source && e.target);

    const _meta = {
      enums: enumNodes.map((en) => ({
        name: en.data.name,
        values: en.data.fields.map((f) => f.name),
        position: { x: Math.round(en.position.x), y: Math.round(en.position.y) },
      })),
      edges: allEdges,
    };

    for (const node of state.nodes) {
      if (node.data.tableType === 'enum') continue;

      const relEdges = state.edges.filter(
        (e) => e.source === node.id || e.target === node.id
      );
      const relationships = [];
      for (const edge of relEdges) {
        // Skip enum connections - they're handled by classType: ENUM
        if (edge.data?.sourceType === 'Enum' || edge.data?.targetType === 'Enum') continue;

        const isSource = edge.source === node.id;
        const thisSide = isSource ? edge.data?.sourceType : edge.data?.targetType;
        const otherSide = isSource ? edge.data?.targetType : edge.data?.sourceType;
        if (thisSide === 'One' && otherSide === 'Many') continue;
        const otherNode = state.nodes.find(
          (n) => n.id === (isSource ? edge.target : edge.source)
        );
        const relType = `${thisSide}To${otherSide}`;
        relationships.push({
          type: relType,
          target: otherNode?.data?.name || 'unknown',
          fieldName: (otherNode?.data?.name || 'unknown').toLowerCase(),
          _sourceHandle: isSource ? edge.sourceHandle : edge.targetHandle,
          _targetHandle: isSource ? edge.targetHandle : edge.sourceHandle,
        });
      }

      const validFields = node.data.fields.map((f) => {
          const field = {
            name: f.name,
            type: f.type,
            classType: 'CLASS',
            nullable: f.nullable,
            unique: f.unique,
          };
          if (enumMap[f.type]) {
            field.classType = 'ENUM';
            field.enumValues = enumMap[f.type];
          }
          return field;
        });

      entities.push({
        name: node.data.name,
        fields: validFields,
        relationships,
        _position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      });
    }

    const project = {
      name: state.projectName,
      basePackage: state.basePackage,
      language: state.language,
      buildTool: state.buildTool,
      packaging: state.packaging,
      projectId: state.projectId || null,
    };
    if (state.language === 'java') {
      project.javaVersion = state.javaVersion;
      project.springBootVersion = state.springBootVersion;
    }

    const result = {
      project,
      database: {
        type: state.database,
        username: state.dbUsername,
        password: state.dbPassword,
      },
      features: state.features,
      entities,
      _meta,
    };

    if (state.features.security) {
      const jwt = {
        expirationMs: parseInt(state.jwtExpiration, 10) || 86400000,
      };
      if (state.jwtAutoGenerate) {
        jwt.autoGenerate = true;
      } else {
        jwt.secretKey = state.jwtSecretKey;
      }
      result.security = { jwt };
    }

    // Full JSON string with positions/meta for backend to store as-is
    result.json = JSON.stringify(result);

    return result;
  },
}));

export default useStore;
